import dateHelper from "~/helpers/dateHelper";
import { productIsEligibleForDownPayment } from "~/helpers/ecommerce/hardwareOrderHelper";

// Partial interfaces
type Prices = {
	fullPrice?: {
		price?: number;
		salePrice?: number;
		memberPrice?: number;
	};
};

type Item = {
	id?: string;
	subItems?: any[];
	metadata?: {
		isVatExempt?: boolean;
		downPayment?: boolean;
		downPaymentPlan?: {
			amountUpFront?: number;
		};
		prices?: {
			withVat?: Prices;
			withoutVat?: Prices;
			memberPriceCampaign?: Campaign;
			campaign?: Campaign;
		};
		price?: number;
	};
};

type Campaign = {
	startDate?: string;
	endDate?: string;
};

const isCampaignActive = (campaign: Campaign): boolean => {
	try {
		if (!campaign || !campaign.startDate) return false;

		const startDate = new Date(campaign.startDate);

		// A campaign may not have an end date, in which case we only check against the start date.
		if (!campaign.endDate) return dateHelper.todayIsAfter(startDate);

		const endDate = new Date(campaign.endDate);
		return dateHelper.todayIsBetween(startDate, endDate);
	} catch (error) {
		console.error("Error parsing campaign dates:", error);
		return false;
	}
};

// Allows passing cart as a parameter (for use after the shopping cart store is cleared upon order completed).
// Otherwise, it reads the shopping cart store directly.
export function usePayNowPrices(cart?: any[]) {
	const { isVatExempt } = useIsVatExempt();
	const { hardwareProducts } = useHardwareCartItems(cart);

	type Options = {
		ignoreDownPayment?: boolean; // E.g. to compute total saved, which is regardless of down payment
		useMemberPrice?: boolean; // E.g. used on product page to display member price, if available, before subscription is in cart
	};

	type PayNowPrices = {
		price: number; // Current price for the customer (which might be equal to nonMemberPrice)
		nonMemberPrice?: number; // Price if you're not a member
		beforePrice?: number; // If there is a discount, this is the price before the discount, otherwise undefined
	};

	const getPayNowPrices = (item: Item, options?: Options): PayNowPrices => {
		// For down payment, you only pay the optional amount up front in the purchase moment
		const isDownPayment = item.metadata?.downPayment && !options?.ignoreDownPayment;
		const amountUpFront = item.metadata?.downPaymentPlan?.amountUpFront || 0;
		if (isDownPayment && productIsEligibleForDownPayment(item)) return { price: amountUpFront };

		// Use VAT-specific prices
		const prices = isVatExempt.value ? item.metadata?.prices?.withoutVat : item.metadata?.prices?.withVat;

		// Normal price
		const normalPrice = prices?.fullPrice?.price;

		// Sale price
		const salePrice = prices?.fullPrice?.salePrice;

		// Member price
		const hasSubscriptionInCart = item.subItems?.some((subItem) => subItem.type === "subscription");
		const campaign = item.metadata?.prices?.memberPriceCampaign;
		const isActive = isCampaignActive(campaign);
		const isEligible = hasSubscriptionInCart || options?.useMemberPrice;
		const memberPrice = isActive && isEligible ? prices?.fullPrice?.memberPrice : undefined;

		const nonMemberPrice = salePrice || normalPrice || item.metadata?.price || 0;
		const price = memberPrice || nonMemberPrice;

		return {
			price,
			nonMemberPrice,
			beforePrice: price != normalPrice ? normalPrice : undefined,
		};
	};

	const totalToPayNow = computed<number>(() =>
		hardwareProducts.value.map((p) => getPayNowPrices(p).price).reduce((a: any, b: any) => a + b, 0),
	);

	const totalSaved = computed<number>(() =>
		hardwareProducts.value
			.map((p) => {
				const { price, beforePrice } = getPayNowPrices(p, { ignoreDownPayment: true });
				return beforePrice ? beforePrice - price : 0;
			})
			.reduce((a: any, b: any) => a + b, 0),
	);

	return { totalToPayNow, totalSaved, getPayNowPrices, isCampaignActive };
}
